import { useCallback, useState } from 'react';
import type { TrackingRange } from '../../types/tracking.type';
import { TRACKING_ERROR_MESSAGES } from '../constants';
type TrackingPropsT = {
  trackingRange: TrackingRange;
  hasConflicts?: (range: TrackingRange) => boolean;
  onTrack?: (range: TrackingRange) => Promise<void>;
  onTrackSuccess?: (range: TrackingRange) => void;
  onTrackError?: (error: string) => void;
};
type SamTrackingResultT = {
  // State
  isTracking: boolean;
  showOverrideModal: boolean;

  // Actions
  startTracking: () => void;
  confirmOverride: () => void;
  cancelOverride: () => void;
};
export const useSamForTracking = ({
  trackingRange,
  hasConflicts,
  onTrack,
  onTrackSuccess,
  onTrackError
}: TrackingPropsT): SamTrackingResultT => {
  const [isTracking, setIsTracking] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [pendingRange, setPendingRange] = useState<TrackingRange | null>(null);
  const executeTracking = useCallback(async (range: TrackingRange) => {
    try {
      setIsTracking(true);
      await onTrack?.(range);
      if (onTrackSuccess) {
        onTrackSuccess(range);
      }
    } catch (err) {
      const errorMessage = TRACKING_ERROR_MESSAGES.TRACKING_FAILED(range);
      if (onTrackError) {
        onTrackError(errorMessage);
      }
    } finally {
      setIsTracking(false);
    }
  }, [onTrack, onTrackSuccess, onTrackError]);
  const startTracking = useCallback(() => {
    if (hasConflicts?.(trackingRange)) {
      setPendingRange(trackingRange);
      setShowOverrideModal(true);
    } else {
      executeTracking(trackingRange);
    }
  }, [trackingRange, executeTracking, hasConflicts]);
  const confirmOverride = useCallback(() => {
    if (pendingRange) {
      setShowOverrideModal(false);
      executeTracking(pendingRange);
      setPendingRange(null);
    }
  }, [pendingRange, executeTracking]);
  const cancelOverride = useCallback(() => {
    setShowOverrideModal(false);
    setPendingRange(null);
  }, []);
  return {
    isTracking,
    showOverrideModal,
    startTracking,
    confirmOverride,
    cancelOverride
  };
};